// pages/saolait/TaoForm.js
// Hướng dẫn tạo form động (dynamic form) từ file JSON.
// Ví dụ xuyên suốt: form Tìm kiếm được sinh ra từ search.json.

import React from "react";

const styles = {
  page: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "24px 20px 64px",
    color: "#1f2937",
    lineHeight: 1.65,
    fontSize: 16,
    textAlign: "left",
  },
  pageTitle: { fontSize: 30, fontWeight: 800, margin: "0 0 4px", color: "#111827" },
  pageSub: { color: "#6b7280", margin: "0 0 24px", fontSize: 15 },
  toc: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "16px 20px",
    margin: "0 0 32px",
  },
  tocTitle: { fontWeight: 700, margin: "0 0 8px" },
  tocLink: { color: "#2563eb", textDecoration: "none" },
  lessonTitle: {
    fontSize: 24,
    fontWeight: 800,
    margin: "40px 0 6px",
    color: "#1d4ed8",
    borderBottom: "2px solid #dbeafe",
    paddingBottom: 6,
  },
  h3: { fontSize: 19, fontWeight: 700, margin: "28px 0 8px", color: "#111827" },
  pre: {
    background: "#0f172a",
    color: "#e2e8f0",
    borderRadius: 10,
    padding: "14px 16px",
    overflowX: "auto",
    fontSize: 13.5,
    lineHeight: 1.5,
    margin: "10px 0 18px",
    textAlign: "left",
    whiteSpace: "pre",
  },
  code: {
    background: "#f1f5f9",
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 14,
    color: "#be123c",
  },
  table: { borderCollapse: "collapse", width: "100%", margin: "10px 0 18px", fontSize: 15 },
  th: { border: "1px solid #d1d5db", padding: "8px 10px", background: "#f3f4f6", textAlign: "left" },
  td: { border: "1px solid #d1d5db", padding: "8px 10px", verticalAlign: "top" },
  goalBox: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: 10,
    padding: "12px 18px",
    margin: "0 0 24px",
  },
  note: {
    background: "#fefce8",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "10px 14px",
    margin: "14px 0",
    fontSize: 15,
  },
  step: {
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: 10,
    padding: "12px 18px",
    margin: "12px 0",
  },
};

// ---- Tô màu cú pháp JSON cho các khối code ----
const jsonToken = {
  key: { color: "#7dd3fc" },      // "tên": (khoá)
  string: { color: "#86efac" },   // "giá trị"
  number: { color: "#fca5a5" },   // số
  keyword: { color: "#c4b5fd" },  // true / false / null
  punct: { color: "#cbd5e1" },    // { } [ ] , :
  comment: { color: "#64748b", fontStyle: "italic" }, // // ghi chú
};

function highlightJson(src) {
  const nodes = [];
  // Thứ tự ưu tiên: comment -> "khoá": -> "chuỗi" -> từ khoá -> số -> dấu câu
  const re =
    /(\/\/[^\n]*)|("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|(\btrue\b|\bfalse\b|\bnull\b)|(-?\d+(?:\.\d+)?)|([{}\[\],:])/g;
  let last = 0;
  let i = 0;
  let m;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) nodes.push(src.slice(last, m.index));
    if (m[1]) {
      nodes.push(<span key={i++} style={jsonToken.comment}>{m[1]}</span>);
    } else if (m[2]) {
      // "khoá" kèm dấu ":" -> tách dấu hai chấm ra
      const colon = m[2].lastIndexOf(":");
      nodes.push(<span key={i++} style={jsonToken.key}>{m[2].slice(0, colon)}</span>);
      nodes.push(<span key={i++} style={jsonToken.punct}>{m[2].slice(colon)}</span>);
    } else if (m[3]) {
      nodes.push(<span key={i++} style={jsonToken.string}>{m[3]}</span>);
    } else if (m[4]) {
      nodes.push(<span key={i++} style={jsonToken.keyword}>{m[4]}</span>);
    } else if (m[5]) {
      nodes.push(<span key={i++} style={jsonToken.number}>{m[5]}</span>);
    } else if (m[6]) {
      nodes.push(<span key={i++} style={jsonToken.punct}>{m[6]}</span>);
    }
    last = re.lastIndex;
  }
  if (last < src.length) nodes.push(src.slice(last));
  return nodes;
}

const Code = ({ children }) => (
  <pre style={styles.pre}>
    <code>{highlightJson(String(children))}</code>
  </pre>
);

const TaoForm = () => (
  <div style={styles.page}>
    <h1 style={styles.pageTitle}>Tạo form động từ file JSON</h1>
    <p style={styles.pageSub}>
      Trong SaolaIT, giao diện không viết cứng bằng React mà được <strong>mô tả bằng một file JSON</strong>.
      Bộ render <span style={styles.code}>DynamicFormCore</span> đọc JSON đó và tự sinh ra giao diện.
      Ví dụ xuyên suốt: form <strong>Tìm kiếm</strong> được sinh ra từ file{" "}
      <span style={styles.code}>search.json</span>.
    </p>

    <div style={styles.goalBox}>
      <strong>Sau bài này bạn sẽ:</strong>
      <ul style={{ margin: "8px 0 0", paddingLeft: 22 }}>
        <li>Hiểu cấu trúc một file JSON mô tả form (<span style={styles.code}>style</span>, <span style={styles.code}>elements</span>, <span style={styles.code}>styles</span>).</li>
        <li>Biết cách khai báo một control và gắn dữ liệu bằng placeholder <span style={styles.code}>{"{field}"}</span>.</li>
        <li>Tự tạo được một form mới và nối vào ứng dụng.</li>
      </ul>
    </div>

    <nav style={styles.toc}>
      <p style={styles.tocTitle}>Nội dung</p>
      <ul style={{ margin: 0, paddingLeft: 22 }}>
        <li><a style={styles.tocLink} href="#c1">1. Ý tưởng: form = dữ liệu (JSON), không phải code</a></li>
        <li><a style={styles.tocLink} href="#c2">2. Bộ khung một file JSON</a></li>
        <li><a style={styles.tocLink} href="#c3">3. elements — cây các control</a></li>
        <li><a style={styles.tocLink} href="#c4">4. style vs styleName</a></li>
        <li><a style={styles.tocLink} href="#c5">5. Gắn dữ liệu: placeholder {"{field}"} và condition</a></li>
        <li><a style={styles.tocLink} href="#c6">6. Nút bấm: submit, action, dataCollection</a></li>
        <li><a style={styles.tocLink} href="#c7">7. Các bước tạo một form mới</a></li>
        <li><a style={styles.tocLink} href="#c8">8. Ví dụ đầy đủ: search.json rút gọn</a></li>
      </ul>
    </nav>

    {/* 1 */}
    <h2 id="c1" style={styles.lessonTitle}>1. Ý tưởng: form = dữ liệu (JSON), không phải code</h2>
    <p>
      Thay vì viết mỗi màn hình bằng một component React riêng, ta mô tả màn hình
      bằng JSON. Một component chung — <span style={styles.code}>DynamicFormCore</span> —
      nhận JSON làm đầu vào, duyệt từng phần tử và render ra HTML tương ứng. Nhờ đó:
    </p>
    <ul>
      <li>Sửa giao diện = sửa JSON, không cần build lại logic.</li>
      <li>Nhiều màn hình (Tìm kiếm, Đăng sách, Tủ sách…) dùng chung một bộ render.</li>
      <li>JSON có thể lưu trên server và tải xuống lúc chạy.</li>
    </ul>
    <div style={styles.note}>
      File JSON của các form nằm ở <span style={styles.code}>src/pages/book_type_sctipt/</span>:
      <span style={styles.code}>search.json</span>, <span style={styles.code}>addbook.json</span>,
      <span style={styles.code}>mybook.json</span>… Xem trang <strong>Control</strong> để biết danh sách control dùng được.
    </div>

    {/* 2 */}
    <h2 id="c2" style={styles.lessonTitle}>2. Bộ khung một file JSON</h2>
    <p>Mỗi file form có 3 khối chính:</p>
    <Code>{`{
  "style":   { ... },      // style của khung ngoài cùng (thẻ <form>)
  "elements": [ ... ],     // DANH SÁCH control hiển thị (bắt buộc)
  "styles":  { ... }       // BẢNG style tái sử dụng, gọi qua "styleName"
}`}</Code>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>Khối</th>
          <th style={styles.th}>Vai trò</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={styles.td}><span style={styles.code}>style</span></td>
          <td style={styles.td}>Style CSS (dạng object) cho vùng bao ngoài của form.</td>
        </tr>
        <tr>
          <td style={styles.td}><span style={styles.code}>elements</span></td>
          <td style={styles.td}>Mảng các control. Đây là phần chính bạn sẽ chỉnh nhiều nhất.</td>
        </tr>
        <tr>
          <td style={styles.td}><span style={styles.code}>styles</span></td>
          <td style={styles.td}>Một "thư viện" style đặt tên. Control tham chiếu bằng <span style={styles.code}>"styleName": "postCardStyle"</span> thay vì lặp lại style dài dòng.</td>
        </tr>
      </tbody>
    </table>

    {/* 3 */}
    <h2 id="c3" style={styles.lessonTitle}>3. elements — cây các control</h2>
    <p>
      Mỗi phần tử trong <span style={styles.code}>elements</span> là một object có
      trường <span style={styles.code}>type</span> để cho biết đó là control gì.
      Control kiểu khung (<span style={styles.code}>container</span>,
      <span style={styles.code}>feed</span>, <span style={styles.code}>dialog</span>)
      có thể chứa <span style={styles.code}>children</span> — tạo thành một cây lồng nhau.
    </p>
    <Code>{`"elements": [
  { "type": "heading", "level": 2, "text": "Tìm sách của người khác" },
  {
    "type": "container",
    "style": { "display": "flex", "gap": "8px" },
    "children": [
      { "type": "input",  "id": "searchOtherBookName", "field": "search_other_book_name",
        "placeholder": "Tìm theo tên sách" },
      { "type": "button", "id": "searchBtn", "buttonType": "submit", "text": "Tìm" }
    ]
  }
]`}</Code>
    <div style={styles.note}>
      Danh sách đầy đủ các <span style={styles.code}>type</span> và tham số của từng cái nằm ở trang{" "}
      <strong>Control</strong> (SaolaIT → Control).
    </div>

    {/* 4 */}
    <h2 id="c4" style={styles.lessonTitle}>4. style và styleName</h2>
    <p>Có 2 cách gán style cho một control:</p>
    <ul>
      <li><span style={styles.code}>"style"</span>: viết trực tiếp object CSS tại chỗ.</li>
      <li><span style={styles.code}>"styleName"</span>: trỏ tới một style đặt tên trong khối <span style={styles.code}>styles</span>.</li>
    </ul>
    <p>
      Nếu khai báo cả hai, <span style={styles.code}>style</span> sẽ <strong>ghi đè</strong> lên
      <span style={styles.code}>styleName</span> (bộ render trộn theo thứ tự <em>styleName trước, style sau</em>).
    </p>
    <Code>{`// Trong "styles":
"postCardStyle": { "background": "#fff", "borderRadius": "12px", "padding": "14px" }

// Trong "elements": dùng lại + chỉnh riêng 1 thuộc tính
{ "type": "container", "styleName": "postCardStyle", "style": { "marginTop": "8px" } }`}</Code>

    {/* 5 */}
    <h2 id="c5" style={styles.lessonTitle}>5. Gắn dữ liệu: placeholder {"{field}"} và condition</h2>
    <p>
      Trong các trường văn bản (<span style={styles.code}>text</span>,
      <span style={styles.code}>src</span>, <span style={styles.code}>targetUid</span>…) bạn có thể
      chèn placeholder dạng <span style={styles.code}>{"{tên}"}</span>. Khi render, bộ render thay
      <span style={styles.code}>{"{tên}"}</span> bằng giá trị dữ liệu tương ứng (dữ liệu của item, của
      context, hoặc dữ liệu đang nhập trong form).
    </p>
    <Code>{`{ "type": "container", "styleName": "bookTitleStyle", "text": "{book_name}" }
{ "type": "image", "src": "{image_url}", "condition": "{image_url}" }`}</Code>
    <p>
      <span style={styles.code}>condition</span> quyết định <strong>có hiển thị control hay không</strong>.
      Control chỉ hiện khi giá trị sau khi thay placeholder khác rỗng và khác
      <span style={styles.code}>false/null/undefined</span>. Ví dụ trên: ảnh chỉ hiện khi có
      <span style={styles.code}>image_url</span>.
    </p>

    {/* 6 */}
    <h2 id="c6" style={styles.lessonTitle}>6. Nút bấm: submit, action, dataCollection</h2>
    <p>Nút <span style={styles.code}>button</span> có 2 kiểu hành vi:</p>
    <ul>
      <li>
        <strong>Gửi form</strong>: đặt <span style={styles.code}>"buttonType": "submit"</span>. Khi bấm,
        form gọi hàm submit và gửi kèm <span style={styles.code}>dataCollection</span> (đường dẫn kho dữ liệu để lưu).
      </li>
      <li>
        <strong>Hành động tuỳ biến</strong>: đặt
        <span style={styles.code}>"action": {'{ "type": "custom", "command": "toggleAdvancedSearch" }'}</span>.
        Khi bấm, bộ render gọi lệnh <span style={styles.code}>command</span> để trang xử lý riêng.
      </li>
    </ul>
    <Code>{`{
  "type": "button", "id": "submitBtn", "buttonType": "submit",
  "dataCollection": "book/input_data_cluster01/draft/book/data/book_items_noa_new",
  "text": "Lưu", "submittingText": "Đang lưu..."
}`}</Code>

    {/* 7 */}
    <h2 id="c7" style={styles.lessonTitle}>7. Các bước tạo một form mới</h2>
    <div style={styles.step}><strong>Bước 1.</strong> Tạo file JSON mới trong <span style={styles.code}>src/pages/book_type_sctipt/</span> (ví dụ <span style={styles.code}>myform.json</span>) với bộ khung 3 khối ở mục 2.</div>
    <div style={styles.step}><strong>Bước 2.</strong> Thêm các control vào <span style={styles.code}>elements</span>. Với mỗi control cần nhập liệu, đặt <span style={styles.code}>id</span> và <span style={styles.code}>field</span>.</div>
    <div style={styles.step}><strong>Bước 3.</strong> Đưa các style dùng lại vào khối <span style={styles.code}>styles</span> và tham chiếu bằng <span style={styles.code}>styleName</span>.</div>
    <div style={styles.step}><strong>Bước 4.</strong> Gắn nút <span style={styles.code}>submit</span> có <span style={styles.code}>dataCollection</span> để lưu dữ liệu.</div>
    <div style={styles.step}><strong>Bước 5.</strong> Tạo một trang React nạp JSON đó qua <span style={styles.code}>DynamicFormCore</span> (xem các trang có sẵn <span style={styles.code}>search_type_script.tsx</span>, <span style={styles.code}>addbook_type_script.tsx</span> để làm mẫu) và khai báo route trong <span style={styles.code}>App.js</span>.</div>

    {/* 8 */}
    <h2 id="c8" style={styles.lessonTitle}>8. Ví dụ đầy đủ: search.json (rút gọn)</h2>
    <p>Ghép mọi thứ lại — một form tìm kiếm tối giản gồm ô nhập + nút Tìm + danh sách kết quả:</p>
    <Code>{`{
  "style": { "padding": "0", "width": "100%" },
  "elements": [
    { "type": "heading", "level": 2, "text": "Tìm sách của người khác" },

    {
      "type": "container",
      "style": { "display": "flex", "gap": "8px", "marginBottom": "16px" },
      "children": [
        { "type": "input", "id": "searchOtherBookName",
          "field": "search_other_book_name", "inputType": "text",
          "placeholder": "Tìm theo tên sách", "style": { "flex": 1 } },
        { "type": "button", "id": "searchBtn", "buttonType": "submit", "text": "Tìm",
          "dataCollection": "book/input_data_cluster01/draft/book/data/searchBtn" }
      ]
    },

    {
      "type": "feed",
      "dataKey": "filteredBooks",
      "cardLayout": {
        "type": "container", "styleName": "postCardStyle",
        "children": [
          { "type": "container", "styleName": "bookTitleStyle", "text": "{book_name}" },
          { "type": "image", "src": "{image_url}", "condition": "{image_url}" }
        ]
      }
    },
    { "type": "pagination" }
  ],
  "styles": {
    "postCardStyle":  { "background": "#fff", "border": "1px solid #e5e7eb",
                        "borderRadius": "12px", "padding": "14px" },
    "bookTitleStyle": { "fontWeight": "700", "marginBottom": "4px" }
  }
}`}</Code>

    <div style={{ ...styles.note, marginTop: 32 }}>
      <strong>Ghi nhớ:</strong> JSON chỉ <em>mô tả</em> giao diện. Logic (lấy dữ liệu, gọi lệnh
      <span style={styles.code}>action</span>, lưu <span style={styles.code}>dataCollection</span>) do
      <span style={styles.code}>DynamicFormCore</span> và trang bao ngoài đảm nhiệm. Muốn biết mỗi control
      nhận tham số gì, mở trang <strong>SaolaIT → Control</strong>.
    </div>
  </div>
);

export default TaoForm;
